import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";
import { today } from "@/components/washes/WashDialog";

export const KINDS = { arrival: "Arrivo AdBlue", refill: "Rifornimento furgone", transfer: "Trasferimento AdBlue" };
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const MoveDialog = ({ move, stations, stockFor, vehicles, isAdmin, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (move) {
      const station_id = move.station_id || stations[0]?.id || "";
      setF({ kind: move.kind, station_id, move_date: today(), liters: move.kind === "refill" ? stockFor(station_id)?.default_liters ?? "" : "", vehicle_id: "", place: "", notes: "" });
    }
  }, [move, stations, stockFor]);
  if (!move || !f) return null;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const st = stockFor(f.station_id);
  const vOpts = vehicles.filter((v) => !isAdmin || v.station_id === f.station_id);
  const valid = f.move_date && Number(f.liters) > 0 && (f.kind !== "refill" || f.vehicle_id) && (f.kind !== "transfer" || f.place.trim()) && (!isAdmin || f.station_id);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/adblue/moves", { ...f, liters: Number(f.liters), vehicle_id: f.vehicle_id || null });
      toast.success(`${KINDS[f.kind]} registrato`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="adblue-move-dialog">
        <DialogHeader>
          <DialogTitle>{KINDS[f.kind]}</DialogTitle>
          <DialogDescription>Giacenza attuale: <b data-testid="adblue-dialog-stock">{st ? `${st.stock.toLocaleString("it-IT")} L` : "—"}</b></DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {isAdmin && <F label="Stazione" span>
            <Select value={f.station_id || undefined} onValueChange={(v) => setF((p) => ({ ...p, station_id: v, vehicle_id: "", liters: p.kind === "refill" ? stockFor(v)?.default_liters ?? "" : p.liters }))}>
              <SelectTrigger data-testid="adblue-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          <F label={f.kind === "arrival" ? "Data arrivo" : "Data"}><Input type="date" max={today()} value={f.move_date} onChange={set("move_date")} data-testid="adblue-date-input" /></F>
          <F label="Litri"><Input type="number" step="0.1" min="0" value={f.liters} onChange={set("liters")} data-testid="adblue-liters-input" /></F>
          {f.kind === "refill" && <F label="Furgone" span><PlateCombobox value={f.vehicle_id} onChange={(v) => setF((p) => ({ ...p, vehicle_id: v }))} testId="adblue-vehicle-select" options={vOpts.map((v) => ({ value: v.id, label: v.plate }))} /></F>}
          {f.kind === "transfer" && <F label="Luogo del trasferimento" span><Input value={f.place} onChange={set("place")} placeholder="Scrivi dove viene trasferito" data-testid="adblue-place-input" /></F>}
          <F label="Note" span><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="adblue-notes-input" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !valid} data-testid="adblue-save-button">{busy ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
